import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface TeamSelectorProps {
  challengeId: string;
  open: boolean;
  onClose: () => void;
}

export function TeamSelector({ challengeId, open, onClose }: TeamSelectorProps) {
  const queryClient = useQueryClient();
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: teams } = useQuery({
    queryKey: ['challenge-teams', challengeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('challenge_teams')
        .select('*, challenge_participants(count)')
        .eq('challenge_id', challengeId);

      if (error) throw error;

      return data.map(team => ({
        ...team,
        member_count: team.challenge_participants?.[0]?.count || 0,
      }));
    },
    enabled: open,
  });

  const joinTeamMutation = useMutation({
    mutationFn: async (teamId: string) => {
      if (!user?.id) throw new Error("Not authenticated");

      // Update participant record with team
      const { error } = await supabase
        .from('challenge_participants')
        .update({ team_id: teamId })
        .eq('challenge_id', challengeId)
        .eq('user_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenge-participants'] });
      queryClient.invalidateQueries({ queryKey: ['challenge-teams'] });
      toast.success("Joined team!");
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to join team");
    },
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Choose Your Team</DialogTitle>
          <DialogDescription>
            Select a team to compete with in this challenge
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {teams?.map((team) => (
            <button
              key={team.id}
              onClick={() => setSelectedTeamId(team.id)}
              className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                selectedTeamId === team.id
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: team.team_color || 'hsl(182,37%,46%)' }}
                  >
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold">{team.team_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {team.member_count} members
                    </p>
                  </div>
                </div>
                {selectedTeamId === team.id && (
                  <Badge variant="default">Selected</Badge>
                )}
              </div>
            </button>
          ))}
        </div>

        <div className="flex gap-3 pt-4">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={() => selectedTeamId && joinTeamMutation.mutate(selectedTeamId)}
            disabled={!selectedTeamId || joinTeamMutation.isPending}
            className="flex-1"
          >
            Join Team
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
